import { api } from "../../api";
import { useState, useCallback } from "react";
import { useFindMany } from "@gadgetinc/react";
import LoadingSpinner from "../LoadingSpinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../tailwind/table";
import { Link } from "../tailwind/link";
import { Button } from "../tailwind/button";
import { Text } from "../tailwind/text";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/16/solid";
import { SegmentFiltersTooltip } from "../SegmentFiltersTooltip";

export function SendingListPast({ campaign, filterDates }) {
  const NUM_ON_PAGE = 10;
  const [cursor, setCursor] = useState({ first: NUM_ON_PAGE });
  const [pageNumber, setPageNumber] = useState(1);

  const [{ data: dataPast, fetching: fetchingPast }] = useFindMany(
    api.sending,
    {
      ...cursor,
      filter: [
        {
          status: { equals: "completed" },
        },
        {
          campaignId: { equals: campaign },
        },
        {
          AND: [
            { sendingDate: { greaterThan: filterDates.startDate } },
            { sendingDate: { lessThan: filterDates.endDate } },
          ],
        },
      ],
      select: {
        id: true,
        contactList: true,
        sendingDate: true,
        contactListSegment: {
          externalId: true,
          type: true,
        },
        segment: {
          id: true,
          name: true,
        },
        segmentFilters: true,
      },
      sort: {
        sendingDate: "Descending",
      },
    },
  );

  const getNextPage = useCallback(() => {
    setCursor({ first: NUM_ON_PAGE, after: dataPast.endCursor });
    setPageNumber(pageNumber + 1);
  }, [dataPast]);

  const getPreviousPage = useCallback(() => {
    setCursor({ last: NUM_ON_PAGE, before: dataPast.startCursor });
    setPageNumber(pageNumber - 1);
  }, [dataPast]);

  return (
    <>
      {fetchingPast && (
        <div className="min-h-[630px]">
          <LoadingSpinner />
        </div>
      )}
      <div className="flex flex-col justify-between">
        {!fetchingPast && (
          <div className="">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader className="w-[30%]">Sending date</TableHeader>
                  <TableHeader className="text-center">Recipients</TableHeader>
                  {!campaign.automationType && (
                    <TableHeader className="w-[60%]">Segment</TableHeader>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {dataPast && dataPast.length > 0 ? (
                  dataPast?.map(
                    ({
                      id,
                      contactList,
                      sendingDate,
                      contactListSegment,
                      segment,
                      segmentFilters,
                    }) => (
                      <TableRow id={id} key={id}>
                        <TableCell className="w-[60%]">
                          {sendingDate.toLocaleDateString("en-US", {
                            weekday: "long",
                            month: "long",
                            day: "numeric",
                            hour: "numeric",
                            minute: "numeric",
                          })}
                        </TableCell>
                        <TableCell className="text-center">
                          {contactList.length}
                        </TableCell>
                        {!campaign.automationType && (
                          <TableCell className="w-[60%]">
                            {segment ? (
                              <SegmentFiltersTooltip
                                segmentFilters={segmentFilters}
                              >
                                <span className="cursor-help">
                                  {segment.name}
                                </span>
                              </SegmentFiltersTooltip>
                            ) : contactListSegment?.type ===
                              "klaviyo-segment" ? (
                              <Link
                                style={{
                                  wordBreak: "break-all",
                                  overflowWrap: "break-word",
                                }}
                                className="hover:underline"
                                href={`https://www.klaviyo.com/list/${contactListSegment?.externalId}`}
                                target="_blank"
                              >
                                {`https://www.klaviyo.com/list/${contactListSegment?.externalId}`}
                              </Link>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ),
                  )
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} className="text-center">
                      No past sendings
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
        <div className="flex justify-between mt-4">
          <Button
            color="secondaryButton"
            onClick={getPreviousPage}
            disabled={!dataPast?.hasPreviousPage}
          >
            <ArrowLeftIcon />
            Previous
          </Button>
          <Text>{pageNumber}</Text>
          <Button
            color="secondaryButton"
            onClick={getNextPage}
            disabled={!dataPast?.hasNextPage}
          >
            Next
            <ArrowRightIcon />
          </Button>
        </div>
      </div>
    </>
  );
}
